import {useEffect, useRef, useState} from 'react'
import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {DEFAULT_SERVICE} from '#/lib/constants'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {logger} from '#/logger'
import {useSetCommunityBrandColor} from '#/state/preferences'
import {useSessionApi} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {isStrictHex6Color} from '#/alf/util/colorGeneration'
import * as Layout from '#/components/Layout'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {type QrLoginTokens} from '#/features/lifeAppLogin/api'
import {useQrLoginPollQuery} from '#/features/lifeAppLogin/useQrLoginSession'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'CommunityLogin'>

/**
 * Headless sign-in landing page for the Life app's embedded WebView. The
 * verkey identifies a QR-login session already created and approved by the
 * mobile app - this screen just polls for the resulting tokens and resumes
 * the session, with no QR code or form ever shown. No `requireAuth` here:
 * this route must be reachable while logged out, since bootstrapping a
 * session from a logged-out state is the entire point.
 */
export function CommunityLoginScreen({route, navigation}: Props) {
  const {t: l} = useLingui()
  const t = useTheme()
  const {resumeSession} = useSessionApi()
  const setCommunityBrandColor = useSetCommunityBrandColor()
  const {verkey, primaryColor} = route.params ?? {}
  const [error, setError] = useState('')
  const consumedRef = useRef(false)

  /*
   * The Life app WebView carries its flavor's brand color in as an untrusted
   * URL param (e.g. `primaryColor=DE4C2C`) - validate strictly before ever
   * persisting or generating colors from it, and do so as one of the first
   * things this screen does so the theme is in place before the user lands
   * on the real feed.
   */
  useEffect(() => {
    if (primaryColor && isStrictHex6Color(primaryColor)) {
      setCommunityBrandColor(primaryColor)
    }
  }, [primaryColor, setCommunityBrandColor])

  const poll = useQrLoginPollQuery({
    serviceUrl: DEFAULT_SERVICE,
    verkey: verkey ?? '',
    enabled: !!verkey,
  })

  const tokens = poll.data?.status === 'ready' ? poll.data.tokens : undefined
  useEffect(() => {
    if (!tokens || consumedRef.current) return
    consumedRef.current = true
    void signInWithTokens(tokens)
    async function signInWithTokens(tokens: QrLoginTokens) {
      try {
        await resumeSession({
          service: DEFAULT_SERVICE,
          did: tokens.did,
          handle: tokens.handle,
          accessJwt: tokens.accessJwt,
          refreshJwt: tokens.refreshJwt,
          emailConfirmed: false,
          active: tokens.active,
        })
        navigation.navigate('Home' as never)
      } catch (err) {
        logger.error('Community login: failed to resume session', {
          safeMessage: err,
        })
        setError(l`Something went wrong signing you in.`)
        consumedRef.current = false
      }
    }
  }, [tokens, resumeSession, navigation, l])

  return (
    <Layout.Screen>
      <View style={[a.flex_1, a.align_center, a.justify_center, a.gap_md]}>
        {!verkey || error || poll.isError ? (
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {error || <Trans>Unable to sign you in.</Trans>}
          </Text>
        ) : (
          <>
            <Loader size="xl" />
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              <Trans>Signing you in…</Trans>
            </Text>
          </>
        )}
      </View>
    </Layout.Screen>
  )
}
